import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ExternalLink, Loader2, Check, Clock } from "lucide-react";

const PILL = {
  verified: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-800",
  not_verified: "bg-secondary text-muted-foreground",
};

/** "Get DBS Verified" call to action. The external provider opens in a NEW tab so the cleaner's
 *  My Clean Connect™ session and onboarding progress are never lost. No provider is invented and
 *  no verification is ever faked — status only becomes Verified on a confirmed provider result. */
export default function DbsVerifyCta({ dbs, onChange, variant = "onboarding" }) {
  const [provider, setProvider] = useState(dbs?.provider || null);
  const [busy, setBusy] = useState(false);
  const [later, setLater] = useState(false);

  useEffect(() => {
    if (dbs?.provider) { setProvider(dbs.provider); return; }
    api.get("/dbs/provider").then((r) => setProvider(r.data)).catch(() => {});
  }, [dbs]);

  const simple = dbs?.simple || { key: "not_verified", label: "Not Verified" };

  const start = async () => {
    setBusy(true);
    // Open the tab synchronously so the browser never blocks it as a popup.
    const tab = provider?.configured ? window.open("about:blank", "_blank", "noopener") : null;
    try {
      const { data } = await api.post("/dbs/start-verification");
      onChange?.(data);
      if (tab && data.provider?.url) tab.location = data.provider.url;
      else toast.info(provider?.unavailable_note || "DBS verification opens shortly.");
    } catch (e) {
      if (tab) tab.close();
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!provider) return <Loader2 className="h-5 w-5 animate-spin text-accent" />;

  return (
    <div className="rounded-2xl border-2 border-accent/30 bg-accent/5 p-5" data-testid="dbs-verify-cta">
      <div className="flex items-start justify-between gap-3">
        <p className="font-display text-lg font-semibold">Get DBS Verified 🛡️</p>
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold shrink-0 ${PILL[simple.key]}`} data-testid="dbs-simple-status">
          {simple.label}
        </span>
      </div>
      <div className="text-sm mt-2 space-y-2">
        {(variant === "onboarding" ? provider.encourage_body : provider.dashboard_body)
          .split("\n\n").map((p) => <p key={p}>{p}</p>)}
      </div>
      {dbs?.mcc_ref && (
        <p className="text-xs text-muted-foreground mt-3" data-testid="dbs-mcc-ref">
          Your My Clean Connect™ reference: <strong>{dbs.mcc_ref}</strong> — quote this so your
          verification result is matched to your account.
        </p>
      )}
      <div className="flex flex-wrap gap-3 mt-4">
        {simple.key !== "verified" && (
          <button onClick={start} disabled={busy}
            className="px-6 py-3 rounded-full bg-accent text-white font-medium inline-flex items-center gap-2 disabled:opacity-50 hover:bg-accent/90 transition-colors"
            data-testid="dbs-get-verified-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
            {provider.cta_label}
          </button>
        )}
        {variant === "onboarding" && simple.key !== "verified" && (
          <button onClick={() => setLater(true)}
            className="px-5 py-3 rounded-full border border-border font-medium hover:bg-secondary transition-colors"
            data-testid="dbs-do-later-btn">
            Do this later
          </button>
        )}
      </div>
      {!provider.configured && (
        <p className="text-xs text-muted-foreground mt-3 inline-flex items-start gap-1.5" data-testid="dbs-provider-note">
          <Clock className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {provider.unavailable_note}
        </p>
      )}
      {later && (
        <div className="mt-4 p-4 rounded-xl bg-background border border-border text-sm space-y-2" data-testid="dbs-later-note">
          {provider.later_body.split("\n\n").map((p) => <p key={p}>{p}</p>)}
        </div>
      )}
      {simple.key === "verified" && (
        <p className="text-sm font-medium text-emerald-700 mt-3 inline-flex items-center gap-1.5" data-testid="dbs-verified-note">
          <Check className="h-4 w-4" /> Your DBS Verified badge is live on your profile.
        </p>
      )}
      <p className="text-xs text-muted-foreground mt-3 inline-flex items-start gap-1.5">
        <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        DBS verification is optional and never blocks your registration. The DBS Verified badge is
        only awarded once a verification result has been confirmed — uploading information or a
        photograph does not verify a certificate.
      </p>
    </div>
  );
}
